import React, { FC, useCallback, useState } from "react";
import { Box, Grid, IconButton, Stack, styled } from "@mui/joy";
import { ViewMode, ViewModeOptions } from "@constants";
import { EmployeeCard } from "@components/EmployeeCard";
import { EmployeesTable } from "@components/EmployeesTable";
import { useAppSelector } from "@store/hooks";
import useLoadData from "@/hooks/useLoadData";
import { getUsers } from "@store/user/actions";
import { selectUsers, selectPagination } from "@/store/user/selectors";
import { IUser } from "@store/user/model";
import { DebounceInput } from "@components/form/DebounceInput";
import { SearchOutlined } from "@mui/icons-material";
import { Loader } from "@components/Loader";
import { Pagination } from "@components/Pagination";
import { EmptyState } from "@components/EmptyState";

type EmployeesProps = {
  onEdit: (employee: IUser) => void;
};

export const Employees: FC<EmployeesProps> = (props) => {
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.LIST);

  const { totalPages, page } = useAppSelector(selectPagination);
  const users = useAppSelector(selectUsers);
  const { loaded, loading, handlePaginate } = useLoadData<IUser>(
    getUsers,
    search,
  );

  const handleSelectMode = useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, []);

  const handleEdit = useCallback((user: IUser) => {
    props.onEdit(user);
  }, []);

  return (
    <>
      <Stack gap={6} direction="row" mb={6}>
        <Input
          variant="plain"
          handleDebounce={setSearch}
          placeholder="Search..."
          startDecorator={<SearchOutlined />}
          sx={{ boxShadow: "sm", bgcolor: "white" }}
        />
        <Box sx={{ borderRadius: "md", boxShadow: "sm" }}>
          {ViewModeOptions.map((mode) => {
            const selected = viewMode === mode.value;
            const Icon = mode.icon;

            return (
              <IconButton
                key={`view-mode-${mode.value}`}
                variant={selected ? "outlined" : "plain"}
                color={selected ? "primary" : "neutral"}
                onClick={() => handleSelectMode(mode.value)}
              >
                <Icon />
              </IconButton>
            );
          })}
        </Box>
      </Stack>
      {loaded && users?.items?.length === 0 ? (
        <EmptyState description="No employees has been added yet." />
      ) : viewMode === ViewMode.GRID ? (
        loading ? (
          <Loader />
        ) : (
          <>
            <Grid container spacing={6} alignItems="stretch">
              {users?.items?.map((user) => (
                <Grid
                  xs={12}
                  xl={3}
                  lg={4}
                  md={6}
                  key={`user-${user.user.id}`}
                  display="flex"
                  alignItems="stretch"
                >
                  <EmployeeCard employee={user} onEdit={handleEdit} />
                </Grid>
              ))}
            </Grid>
            <Stack alignItems="center" mt={6}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, page) => handlePaginate(page)}
                color="primary"
                disabled={loading}
              />
            </Stack>
          </>
        )
      ) : (
        <EmployeesTable
          employees={users}
          onPaginate={handlePaginate}
          currentPage={page}
          totalPages={totalPages}
          onEdit={handleEdit}
        />
      )}
    </>
  );
};

const Input = styled(DebounceInput)`
  flex: 1;
`;
